import { useLocation, useNavigationType } from "react-router-dom";
import { useEffect, useRef } from "react";

const TAB_PATHS = [
  "/connect-portal",
  "/connect-portal/dashboard",
  "/connect-portal/messages",
  "/connect-portal/dial",
  "/connect-portal/history",
  "/connect-portal/me",
];

function isTab(pathname: string): boolean {
  // Strip query
  const p = pathname.split("?")[0].replace(/\/$/, "");
  return TAB_PATHS.some((t) => p === t);
}

export type NavDirection = "push" | "pop" | "tab" | "replace" | "initial";

/**
 * Phase 2 - derives push / pop / tab transition intent from React Router
 * navigation type. Tabs cross-dissolve, push slides right→left, pop reverses.
 */
export function useNavigationDirection(): NavDirection {
  const location = useLocation();
  const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
  const prev = useRef<{ path: string; key: string } | null>(null);
  const direction = useRef<NavDirection>("initial");

  useEffect(() => {
    const cur = { path: location.pathname, key: location.key };
    const previous = prev.current;
    if (!previous) {
      direction.current = "initial";
    } else if (navType === "REPLACE") {
      direction.current = "replace";
    } else if (isTab(cur.path) && isTab(previous.path)) {
      direction.current = "tab";
    } else if (navType === "POP") {
      direction.current = "pop";
    } else {
      direction.current = "push";
    }
    prev.current = cur;
  }, [location.key, location.pathname, navType]);

  // Initial render: compute synchronously so first paint has a value
  if (!prev.current) {
    prev.current = { path: location.pathname, key: location.key };
  }
  return direction.current;
}

export { isTab as isTabPath };
